import Button from "../../Components/Button";
import DatePicker from "../../Components/DatePicker";
import Modal from "../../Components/Modal";
import {
  composePollNameFromSuffix,
  quarterPrefixFromPeriod,
  todayDateString,
  type LaunchContextAudienceOption,
  type LaunchPollFormState,
} from "../../Utils/Helper/sponsorEngagementLaunch";
import { toast } from "../../Utils/Helper/toast";

export type { LaunchPollFormState };

const LAUNCH_POLL_TYPES = [
  { key: "poll1" as const, label: "Poll 1 — Executive Sponsors" },
  { key: "poll2" as const, label: "Poll 2 — Account Team Members" },
] as const;

const POLL_UNAVAILABLE_MESSAGES = {
  poll1: "This audience has already been used for Poll 1 in the current cycle.",
  poll2: "This audience has already been used for Poll 2 in the current cycle.",
} as const;

const POLL1_ALREADY_LAUNCHED_SOFT =
  "Poll 1 has already been launched this cycle.";

const BOTH_POLLS_WARNING = "Please complete Poll 1 before starting Poll 2.";

function pollTypeBlockMessage(
  pollKey: "poll1" | "poll2",
  canLaunch: boolean,
  blockReason: string | null | undefined,
  otherPollCanLaunch: boolean
): string | null {
  if (canLaunch) {
    return null;
  }
  if (pollKey === "poll1" && otherPollCanLaunch) {
    return POLL1_ALREADY_LAUNCHED_SOFT;
  }
  return blockReason ?? POLL_UNAVAILABLE_MESSAGES[pollKey];
}

type LaunchPollModalProps = {
  isOpen: boolean;
  onClose: () => void;
  form: LaunchPollFormState;
  launchPeriod: string;
  poll1AudienceOptions: LaunchContextAudienceOption[];
  poll2AudienceOptions: LaunchContextAudienceOption[];
  poll1CycleEndDate?: string;
  poll2CycleEndDate?: string;
  poll1CanLaunch?: boolean;
  poll2CanLaunch?: boolean;
  poll1BlockReason?: string | null;
  poll2BlockReason?: string | null;
  onPollTypeToggle: (pollKey: "poll1" | "poll2", enabled: boolean) => void;
  onChange: (patch: Partial<LaunchPollFormState>) => void;
  onLaunch: () => void;
  launching?: boolean;
};

const PollSection = ({
  title,
  audienceLabel,
  audienceId,
  audienceValue,
  audienceOptions,
  dueDateId,
  dueDateValue,
  minDueDate,
  maxDueDate,
  fieldsDisabled,
  onAudienceChange,
  onDueDateChange,
}: {
  title: string;
  audienceLabel: string;
  audienceId: string;
  audienceValue: string;
  audienceOptions: LaunchContextAudienceOption[];
  dueDateId: string;
  dueDateValue: string;
  minDueDate: string;
  maxDueDate?: string;
  fieldsDisabled: boolean;
  onAudienceChange: (value: string) => void;
  onDueDateChange: (value: string) => void;
}) => {
  const selected = audienceOptions.find(opt => opt.value === audienceValue);
  const audienceInvalid = !selected || selected.disabled === true;

  return (
    <div className="flex flex-col gap-5">
      <h4 className="m-0 text-sm font-semibold text-gray-800">{title}</h4>
      <div className="custom-input relative">
        <label htmlFor={audienceId} className="custom-input__label">
          {audienceLabel} <span className="text-red-500">*</span>
        </label>
        <select
          id={audienceId}
          className={`custom-input__container custom-input__height ${
            audienceInvalid ? "border-red-500" : ""
          }`}
          value={audienceValue}
          disabled={fieldsDisabled}
          onChange={e => onAudienceChange(e.target.value)}
        >
          {audienceOptions.map(opt => (
            <option
              key={opt.value}
              value={opt.value}
              disabled={opt.disabled}
              title={opt.disabled_reason ?? undefined}
            >
              {opt.label}
            </option>
          ))}
        </select>
        {audienceInvalid ? (
          <p className="mt-1 mb-0 text-xs text-amber-700">
            {selected?.disabled_reason ??
              "Selected audience is not available for this cycle."}
          </p>
        ) : null}
      </div>
      <DatePicker
        label="Due Date"
        name={dueDateId}
        required
        value={dueDateValue}
        min={minDueDate}
        max={maxDueDate}
        disabled={fieldsDisabled}
        onChange={e => {
          const value = e.target.value;
          if (value && value < minDueDate) return;
          if (maxDueDate && value && value > maxDueDate) return;
          onDueDateChange(value);
        }}
      />
    </div>
  );
};

const LaunchPollModal = ({
  isOpen,
  onClose,
  form,
  launchPeriod,
  poll1AudienceOptions,
  poll2AudienceOptions,
  poll1CycleEndDate,
  poll2CycleEndDate,
  poll1CanLaunch = false,
  poll2CanLaunch = false,
  poll1BlockReason,
  poll2BlockReason,
  onPollTypeToggle,
  onChange,
  onLaunch,
  launching = false,
}: LaunchPollModalProps) => {
  const primaryDisabled = launching;
  const fieldsDisabled = launching;
  const minDueDate = todayDateString();
  const pollNamePrefix = quarterPrefixFromPeriod(launchPeriod);

  const poll1Selected = poll1AudienceOptions.find(
    opt => opt.value === form.poll1_target_audience
  );
  const poll2Selected = poll2AudienceOptions.find(
    opt => opt.value === form.poll2_target_audience
  );
  const showPoll1Section = form.poll1_enabled && poll1CanLaunch;
  const showPoll2Section = form.poll2_enabled && poll2CanLaunch;
  const poll1AudienceInvalid =
    showPoll1Section && (!poll1Selected || poll1Selected.disabled === true);
  const poll2AudienceInvalid =
    showPoll2Section && (!poll2Selected || poll2Selected.disabled === true);
  const noPollType = !showPoll1Section && !showPoll2Section;
  const showBothPollsWarning = showPoll1Section && showPoll2Section;

  return (
    <Modal
      isOpen={isOpen}
      toggle={() => {
        if (!launching) onClose();
      }}
      usePortal
      size="6xl"
      title="Launch New Poll Cycle"
      subtitle="Name the poll, choose poll type(s), audience, and due date(s)."
      onCloseButton={launching ? undefined : onClose}
      footer={
        <div className="flex justify-end gap-2 w-full">
          <Button variant="secondary" onClick={onClose} disabled={launching}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onLaunch}
            loading={launching}
            disabled={
              primaryDisabled ||
              noPollType ||
              poll1AudienceInvalid ||
              poll2AudienceInvalid
            }
          >
            Launch Poll
          </Button>
        </div>
      }
    >
      <div
        className={`text-sm text-gray-700 ${fieldsDisabled ? "pointer-events-none opacity-65" : ""}`}
      >
        <div className="flex flex-col gap-6">
          <div className="custom-input relative">
            <label
              htmlFor="launch-poll-name-suffix"
              className="custom-input__label"
            >
              Poll Name
            </label>
            <div className="custom-input__container custom-input__height flex w-full min-w-0 items-center overflow-hidden">
              <span
                className="shrink-0 select-none text-gray-400 whitespace-pre"
                aria-hidden
              >
                {`${pollNamePrefix}\u00a0`}
              </span>
              <input
                id="launch-poll-name-suffix"
                type="text"
                className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none disabled:cursor-not-allowed"
                value={form.poll_name_suffix}
                disabled={fieldsDisabled}
                aria-label={`Poll name after ${pollNamePrefix}`}
                placeholder="e.g. Customer Satisfaction"
                onChange={e =>
                  onChange({
                    poll_name_suffix: e.target.value,
                    poll_name_touched: true,
                  })
                }
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <span
              id="launch-poll-type-label"
              className="text-sm font-medium text-gray-700"
            >
              Poll Type
            </span>
            <ul
              className="m-0 flex flex-col gap-2 text-sm text-gray-700 list-none pl-0"
              aria-labelledby="launch-poll-type-label"
            >
              {LAUNCH_POLL_TYPES.map(poll => {
                const enabled =
                  poll.key === "poll1"
                    ? form.poll1_enabled
                    : form.poll2_enabled;
                const canLaunch =
                  poll.key === "poll1" ? poll1CanLaunch : poll2CanLaunch;
                const otherPollCanLaunch =
                  poll.key === "poll1" ? poll2CanLaunch : poll1CanLaunch;
                const blockReason =
                  poll.key === "poll1" ? poll1BlockReason : poll2BlockReason;
                const displayBlockReason = pollTypeBlockMessage(
                  poll.key,
                  canLaunch,
                  blockReason,
                  otherPollCanLaunch
                );
                const showBlockedToast = () => {
                  if (fieldsDisabled || canLaunch || !displayBlockReason) {
                    return;
                  }
                  toast.info(displayBlockReason);
                };
                return (
                  <li key={poll.key} className="flex flex-col gap-1">
                    <div
                      className="flex items-center gap-2"
                      onClick={showBlockedToast}
                    >
                      <input
                        id={`launch-${poll.key}`}
                        type="checkbox"
                        className={`h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 ${
                          !canLaunch ? "pointer-events-none" : ""
                        }`}
                        checked={enabled && canLaunch}
                        disabled={fieldsDisabled || !canLaunch}
                        onChange={e => {
                          if (!canLaunch) return;
                          onPollTypeToggle(poll.key, e.target.checked);
                        }}
                      />
                      <label
                        htmlFor={canLaunch ? `launch-${poll.key}` : undefined}
                        className={`font-normal text-gray-800 ${
                          canLaunch ? "cursor-pointer" : "cursor-not-allowed"
                        }`}
                        onClick={e => {
                          if (!canLaunch) {
                            e.preventDefault();
                          }
                        }}
                      >
                        {poll.label}
                      </label>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {noPollType ? (
          <p className="mt-3 mb-0 text-xs text-amber-700">
            Select at least one poll type.
          </p>
        ) : null}

        {showBothPollsWarning ? (
          <p
            className="mt-3 mb-0 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3"
            role="status"
          >
            {BOTH_POLLS_WARNING}
          </p>
        ) : null}

        {(showPoll1Section || showPoll2Section) && (
          <div
            className={`mt-5 grid gap-6 ${
              showPoll1Section && showPoll2Section
                ? "sm:grid-cols-2 sm:divide-x sm:divide-gray-200"
                : "grid-cols-1"
            }`}
          >
            {showPoll1Section ? (
              <div
                className={
                  showPoll2Section
                    ? "sm:pr-6 max-sm:border-b max-sm:border-gray-200 max-sm:pb-6"
                    : ""
                }
              >
                <PollSection
                  title="Poll 1"
                  audienceLabel="Target Audience"
                  audienceId="launch-poll1-audience"
                  audienceValue={form.poll1_target_audience}
                  audienceOptions={poll1AudienceOptions}
                  dueDateId="launch-poll1-due-date"
                  dueDateValue={form.poll1_due_date}
                  minDueDate={minDueDate}
                  maxDueDate={poll1CycleEndDate}
                  fieldsDisabled={fieldsDisabled}
                  onAudienceChange={value =>
                    onChange({ poll1_target_audience: value })
                  }
                  onDueDateChange={value => onChange({ poll1_due_date: value })}
                />
              </div>
            ) : null}
            {showPoll2Section ? (
              <div className={showPoll1Section ? "sm:pl-6" : ""}>
                <PollSection
                  title="Poll 2"
                  audienceLabel="Target Audience"
                  audienceId="launch-poll2-audience"
                  audienceValue={form.poll2_target_audience}
                  audienceOptions={poll2AudienceOptions}
                  dueDateId="launch-poll2-due-date"
                  dueDateValue={form.poll2_due_date}
                  minDueDate={minDueDate}
                  maxDueDate={poll2CycleEndDate}
                  fieldsDisabled={fieldsDisabled}
                  onAudienceChange={value =>
                    onChange({ poll2_target_audience: value })
                  }
                  onDueDateChange={value => onChange({ poll2_due_date: value })}
                />
              </div>
            ) : null}
          </div>
        )}

        <p className="mt-4 mb-0 text-xs leading-relaxed text-gray-500">
          Poll frequencies are configured in Vendor Admin → Settings → Sponsor
          Engagement Discovery. Templates are managed in the Assessment Template
          Library.
        </p>

        {form.poll_name_suffix.trim() ? (
          <p className="mt-2 mb-0 text-xs text-gray-400">
            Preview:{" "}
            {composePollNameFromSuffix(launchPeriod, form.poll_name_suffix)}
          </p>
        ) : null}
      </div>
    </Modal>
  );
};

export default LaunchPollModal;
